import {
  Box,
  Button,
  ButtonBase,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { ArrowBack, ArrowForward } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { RootState } from "config/store";
import { format } from "date-fns";
import { round } from "lodash";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { listItemsInWarehousePageable } from "shared/network/warehouse.api";
import { ItemPriceHistory, ItemTaxHistory, Warehouse } from "shared/types";
import { useDebouncedCallback } from "use-debounce";
import SellingFormItemItem from "./SellingFormItemItem";

const SellingFormItemList = ({
  selectedCategory,
  selectedCurrency,
  append,
}: {
  selectedCategory: any;
  selectedCurrency: any;
  append: any;
}) => {
  const { t } = useTranslation();
  const { selectedRelTenant } = useSelector((state: RootState) => state.authentication);
  const tenant = selectedRelTenant?.tenant;
  const form = useFormContext();
  const warehouse = form.watch("warehouse") as Warehouse;

  const [currentPage, setCurrentPage] = useState(0);
  const [itemSearch, setItemSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false); // Modális állapot

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Kis képernyő ellenőrzése

  const stockQuery = useQuery(
    ["stockQuery", tenant.id, warehouse?.id, currentPage, selectedCategory, itemSearch],
    async () => {
      const { data } = await listItemsInWarehousePageable(
        currentPage,
        12,
        tenant.id,
        `warehouseId:${warehouse?.id};date=${format(new Date(), "yyyy-MM-dd")};quantity!=0;${
          itemSearch ? `item.name:${itemSearch}` : ""
        }`,
        "item.name,asc",
        selectedCategory?.name || "",
      );
      return data?.page;
    },
    { enabled: !!warehouse?.id && !!tenant.id },
  );

  const items = stockQuery.data?.content || [];
  const totalPages = stockQuery?.data?.totalPages || 0;

  const handleItemSearchChange = useDebouncedCallback((value: string) => {
    if (value.length >= 3 || value.length === 0) {
      setItemSearch(value);
    }
  }, 500);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedCategory]);

  const renderPagination = () => (
    <>
      {totalPages > 1 && (
        <ButtonBase
          disabled={currentPage === 0}
          onClick={() => {
            if (currentPage > 0) {
              setCurrentPage(currentPage - 1);
            }
          }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: "50px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <ArrowBack style={{ fontSize: "24px", color: currentPage === 0 ? "gray" : "black" }} />
        </ButtonBase>
      )}
      {totalPages > 1 && (
        <ButtonBase
          disabled={currentPage === totalPages - 1}
          onClick={() => {
            if (currentPage < totalPages - 1) {
              setCurrentPage(currentPage + 1);
            }
          }}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            height: "100%",
            width: "50px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <ArrowForward
            style={{
              fontSize: "24px",
              color: currentPage === totalPages - 1 ? "gray" : "black",
            }}
          />
        </ButtonBase>
      )}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" width="100%" mt={1}>
          <Typography>
            {currentPage + 1}/{totalPages}
          </Typography>
        </Box>
      )}
    </>
  );

  const renderItems = () => (
    <Grid container spacing={2}>
      {stockQuery.isFetching ? (
        Array.from({ length: 12 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Skeleton
              variant="rect"
              height={100}
              style={{
                borderRadius: "4px",
              }}
            />
          </Grid>
        ))
      ) : items.length === 0 ? (
        <Grid item xs={12}>
          <Typography
            style={{
              textAlign: "center",
              fontWeight: "bold",
              fontSize: 16,
              color: "gray",
            }}
          >
            {t("Nincs elérhető termék.")}
          </Typography>
        </Grid>
      ) : (
        items.map(({ item }, index) => {
          const priceHistory = item.itemPriceHistories.find(
            (price: ItemPriceHistory) =>
              price.isActive && selectedCurrency.id === price?.currency?.id,
          );
          const price = priceHistory?.price ? Number(priceHistory?.price) : 0;
          const tax =
            (item?.itemTaxHistories?.find((tax: ItemTaxHistory) => tax.isActive)?.tax?.percent ||
              0) / 100 || 0;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <SellingFormItemItem
                item={item}
                setIsDialogOpen={setIsDialogOpen}
                price={price}
                tax={tax}
                selectedCategory={selectedCategory}
                selectedCurrency={selectedCurrency}
                append={append}
              />
            </Grid>
          );
        })
      )}
    </Grid>
  );

  return (
    <Box position="relative" pb={2}>
      <Typography variant="h6" style={{ fontSize: 16, padding: 8 }}>
        Termékek
      </Typography>
      {isSmallScreen ? (
        <>
          {/* Gomb a modális megnyitásához */}
          <Box style={{ padding: "0 16px" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsDialogOpen(true)}
              fullWidth
            >
              {t("Termékek megtekintése")}
            </Button>
          </Box>
          <Dialog
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>{t("Termékek")}</DialogTitle>
            <DialogContent>
              <Box style={{ paddingBottom: 8 }}>
                <TextField
                  label={t("Keresés")}
                  onChange={event => handleItemSearchChange(event.target.value)}
                  fullWidth
                />
              </Box>
              {renderItems()}
              {renderPagination()}
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="flex-start"
          alignItems="stretch"
          gridGap={16}
          mt={2}
          style={{ margin: "0 50px" }}
        >
          <Box style={{ width: "100%" }}>
            <TextField
              label={t("Keresés")}
              onChange={event => handleItemSearchChange(event.target.value)}
              fullWidth
            />
          </Box>
          {renderItems()}
          {renderPagination()}
        </Box>
      )}
    </Box>
  );
};

export default SellingFormItemList;
